import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowsLeftRight, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useProduct } from "@/context/ProductContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GlobalCoverage = ({ onCompare }) => {
  const [c, setC] = useState(null);
  useEffect(() => { axios.get(`${API}/global/coverage`).then((r) => setC(r.data)).catch(console.error); }, []);
  if (!c) return null;
  const stats = [
    ["Markets supported", c.total_markets],
    ["Structured HTA systems", c.structured_hta],
    ["Formal economic evaluation", c.formal_economic_evaluation],
    ["Procurement / hospital driven", c.procurement_driven],
    ["Accept international evidence", c.accept_international_evidence],
    ["Require local evidence", c.require_local_evidence],
    ["Managed entry agreements", c.mea_supported],
    ["Accelerated pathways", c.accelerated_pathways],
  ];
  return (
    <section data-testid="global-coverage" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-heading font-bold text-sm tracking-tight">Global Coverage</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {Object.entries(c.regions).filter(([, n]) => n > 0).map(([r, n]) => `${r} ${n}`).join(" · ")}
            {" — "}{Object.entries(c.regions).filter(([, n]) => n === 0).map(([r]) => r).join(", ")} expansion-ready. The registry scales to 100+ markets without architectural change.
          </p>
        </div>
        <button data-testid="compare-markets-btn" onClick={onCompare}
          className="text-xs font-semibold text-slate-900 border border-slate-300 px-4 py-2.5 hover:bg-slate-50 flex items-center gap-2">
          <ArrowsLeftRight size={14} /> Compare Markets
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 divide-x divide-slate-100">
        {stats.map(([label, value]) => (
          <div key={label} className="px-4 py-3.5">
            <p className="font-heading font-extrabold text-2xl">{value}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5 leading-tight">{label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export const CompareDialog = ({ countries, onClose }) => {
  const { productId } = useProduct();
  const [a, setA] = useState(countries[0]?.id || "");
  const [b, setB] = useState(countries[1]?.id || "");
  const [r, setR] = useState(null);

  useEffect(() => {
    if (!a || !b || a === b) return setR(null);
    axios.get(`${API}/global/compare`, { params: { a, b, product_id: productId } }).then((res) => setR(res.data)).catch(console.error);
  }, [a, b, productId]);

  const sel = "text-xs border border-slate-300 px-2.5 py-2 bg-white flex-1";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="compare-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading text-sm">Global Market Comparison</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3">
          <select data-testid="compare-select-a" className={sel} value={a} onChange={(e) => setA(e.target.value)}>
            {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <ArrowsLeftRight size={16} className="text-slate-400 shrink-0" />
          <select data-testid="compare-select-b" className={sel} value={b} onChange={(e) => setB(e.target.value)}>
            {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {r && (
          <div data-testid="compare-result" className="max-h-[55vh] overflow-y-auto space-y-4">
            <div className="bg-slate-900 text-white p-4">
              <p className="text-xs">
                <span className="font-bold">{r.a.flag} {r.a.name}</span> ({r.a.hta_body}) vs <span className="font-bold">{r.b.flag} {r.b.name}</span> ({r.b.hta_body})
                <span className="font-mono-data font-bold text-emerald-400 ml-2">{r.similarity}% structural similarity</span>
              </p>
              <p className="text-[11px] text-white/60 mt-1">{r.verdict}</p>
            </div>
            {r.shared_requirements.length > 0 && (
              <div>
                <p className="overline-label text-slate-400">Shared evidence expectations</p>
                <div className="mt-1.5 space-y-1">
                  {r.shared_requirements.map((s) => (
                    <p key={s.type} className="text-[11px] text-slate-600 flex gap-2">
                      {s.aligned ? <CheckCircle size={13} weight="bold" className="text-emerald-600 shrink-0 mt-0.5" /> : <XCircle size={13} className="text-amber-600 shrink-0 mt-0.5" />}
                      <span><span className="font-semibold">{s.type}</span> — {s.note}</span>
                    </p>
                  ))}
                </div>
              </div>
            )}
            {r.differences.length > 0 && (
              <div>
                <p className="overline-label text-slate-400">Market-specific requirements</p>
                <div className="mt-1.5 space-y-1">
                  {r.differences.map((d) => (
                    <p key={d.type + d.required_by} className="text-[11px] text-slate-600">
                      <span className="font-semibold">{d.type}</span> ({d.level}) — only {d.required_by}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="overline-label text-slate-400">Classification alignment</p>
              <div className="mt-1.5 divide-y divide-slate-100 border border-slate-200">
                {r.classification_comparison.map((c) => (
                  <div key={c.dimension} className={`px-3 py-1.5 grid grid-cols-3 gap-2 text-[11px] ${c.aligned ? "" : "bg-amber-50/60"}`}>
                    <span className="font-semibold text-slate-700">{c.dimension}</span>
                    <span className="text-slate-500">{c.a}</span>
                    <span className="text-slate-500">{c.b}</span>
                  </div>
                ))}
              </div>
            </div>
            {r.localization_needs.length > 0 && (
              <div>
                <p className="overline-label text-slate-400">Localization needs</p>
                {r.localization_needs.map((l, i) => <p key={i} className="text-[11px] text-slate-600 mt-1">› {l}</p>)}
              </div>
            )}
          </div>
        )}
        {a === b && <p className="text-xs text-slate-400 text-center py-4">Pick two different markets to compare.</p>}
      </DialogContent>
    </Dialog>
  );
};
